// The viewer's saved lists (favorites, watch-later) — the shared vocabulary.
//
// One API shape serves both features: they differ by a path segment and nothing
// else (see documentation/spec-user-lists-api.md). A third list ("seen", "hidden")
// is therefore a value here, not a new module.

import type { MediaEntry } from '@medialib/styleguide'

/** The lists the API knows about — the `{list}` path segment. */
export type ListName = 'favorites' | 'watchlater'

/** Every list, in the order the manifest declares them. */
export const LIST_NAMES: ListName[] = ['favorites', 'watchlater']

/**
 * `GET /api/3.0/profile/lists` — membership only, ids, unpaged, newest-first.
 *
 * This is what the *toggles* read: every tile in browse, search and the detail page
 * renders a heart and a clock, and they must not each cost a request. Both keys are
 * always present (empty arrays included), so a missing key is a bug, not an empty list.
 */
export type ListManifest = Record<ListName, string[]>

/** One resolved entry of a list — the same object the API nests under `items`. */
export interface ListItem {
  /**
   * The saved title, in the shared media shape, **always including `player`** — a
   * watch-later tile that has been partially watched keeps its progress bar. Maps to
   * a tile through the shared `createMovieTileProps`, no per-endpoint adapter.
   */
  movie: MediaEntry;
  /** When it was saved, extended ISO-8601. Re-adding does not move it. */
  added_at: string;
}

/** The paging envelope, identical to `player/history`'s so the paging code is shared. */
export interface ListPageInfo {
  total_pages: number;
  total: number;
  page: number;
  has_more: boolean;
}

/** `GET /api/3.0/profile/list/{list}` — resolved titles, newest-added first. */
export interface ListPage {
  page: ListPageInfo;
  items: ListItem[];
}

export interface ListPageOptions {
  /** 1-based, defaults to 1. */
  page?: number;
  /** Defaults to 40 at the API, capped there at 100. */
  limit?: number;
}

/**
 * What one toggle needs to know about one title, from the shell's single manifest.
 *
 * Split into `available` and `ready` on purpose: a signed-out viewer has nowhere to
 * save to and the toggle hides itself, while a signed-in viewer whose manifest is
 * still in flight gets the toggle in its inactive state rather than a hole in the
 * tile that fills in a moment later.
 */
export interface ListMembership {
  /** Is this title in the list? Optimistic — flips before the request settles. */
  active: boolean;
  /** Has the manifest landed? False while the first request is in flight. */
  ready: boolean;
  /** Can this viewer save at all? False when anonymous or the manifest failed. */
  available: boolean;
  /** Is a write for this exact (list, title) in flight? */
  pending: boolean;
  /**
   * Flip membership: optimistic locally, then the API call, rolled back on failure.
   * Never rejects — a failed write reports itself to telemetry and restores the
   * previous state, because a toggle that throws into an onClick handler is a crash.
   */
  toggle (): Promise<void>;
}

/**
 * The membership of one whole list, as the manifest knows it — ids only.
 *
 * This is what a *list page* watches to know it must reload: a title saved from the
 * hero has to appear in the favorites row without a refresh, and one un-saved from a
 * tile has to leave it. `ids` keeps a stable identity while the list is unchanged, so
 * it can be used directly as an effect dependency.
 */
export interface ListIdsState {
  ids: string[];
  /** Has the manifest landed? */
  ready: boolean;
  /** Is there an account to have a list at all? */
  available: boolean;
}

/** The saved-lists surface the shell adds to every pilet's API. */
export interface PiletListsApi {
  /**
   * One page of a list, resolved: the titles themselves, not ids. Rejects with a
   * {@link ListRequestError} carrying the status — a signed-out viewer gets 401,
   * which the caller must be able to tell from "nothing saved" (`items: []`).
   */
  getList (list: ListName, options?: ListPageOptions): Promise<ListPage>;
  /**
   * Save a title. Idempotent at the API, and the shared manifest is updated so every
   * toggle for that title, in every pilet, flips at once.
   */
  addToList (list: ListName, movieId: string): Promise<void>;
  /** Remove a title. `204` for a title that was not there, so this is idempotent too. */
  removeFromList (list: ListName, movieId: string): Promise<void>;
  /**
   * Live membership of one title in one list, read from the shell's single manifest.
   * This is what a toggle renders from — see the styleguide's `createListToggle`.
   */
  useListMembership (list: ListName, movieId: string): ListMembership;
  /**
   * Live membership of a whole list, ids only, from the same shared manifest. A list
   * page reloads its resolved titles when this changes — see {@link ListIdsState}.
   */
  useListIds (list: ListName): ListIdsState;
}
